import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { AlertTriangle, X, ShieldAlert, Loader2, CheckCircle2, Info } from 'lucide-react';

type ActionVariant = 'danger' | 'warning' | 'info' | 'default';

interface ActionOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ActionVariant;
  action?: () => Promise<any>;
  successMessage?: string;
  requireConfirmation?: boolean; // If false, executes immediately (useful for complex saves)
  keepOpenOnSuccess?: boolean;
}

interface AlertOptions {
  title: string;
  message: string;
  variant?: ActionVariant;
  confirmLabel?: string;
}

interface ActionFlowContextType {
  startAction: (options: ActionOptions) => Promise<boolean>;
  openAlert: (options: AlertOptions) => Promise<void>;
}

const ActionFlowContext = createContext<ActionFlowContextType | undefined>(undefined);

export const useActionFlow = () => {
  const context = useContext(ActionFlowContext);
  // Fallback for safety if provider is missing
  if (!context) {
    console.warn("ActionFlowProvider missing. Falling back to native dialogs.");
    return {
      startAction: async (opts: ActionOptions) => {
        if (opts.requireConfirmation !== false) {
          if (!window.confirm(`${opts.title}\n\n${opts.message}`)) return false;
        }
        try {
          if (opts.action) await opts.action();
          if (opts.successMessage) window.alert(opts.successMessage);
          return true;
        } catch (e: any) {
          window.alert(`Action Failed: ${e.message}`);
          return false;
        }
      },
      openAlert: async (opts: AlertOptions) => {
        window.alert(`${opts.title}\n\n${opts.message}`);
      }
    };
  }
  return context;
};

export const ActionFlowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<'CONFIRMING' | 'EXECUTING' | 'SUCCESS' | 'ERROR'>('CONFIRMING');
  const [config, setConfig] = useState<ActionOptions | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

  // For simple alerts
  const [isAlertMode, setIsAlertMode] = useState(false);

  const startAction = useCallback((options: ActionOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfig(options);
      setResolvePromise(() => resolve);
      setErrorMsg("");
      setIsAlertMode(false);
      
      if (options.requireConfirmation === false) {
        setState('EXECUTING');
        setIsOpen(true);
        executeAction(options, resolve);
      } else {
        setState('CONFIRMING');
        setIsOpen(true);
      }
    });
  }, []);

  const openAlert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      setConfig({
        ...options,
        action: async () => {}, // No-op
        requireConfirmation: true
      });
      setResolvePromise(() => () => resolve());
      setErrorMsg("");
      setIsAlertMode(true);
      setState('CONFIRMING');
      setIsOpen(true);
    });
  }, []);

  const executeAction = async (currentConfig: ActionOptions, resolver: (val: boolean) => void) => {
    if (!currentConfig.action) {
      handleClose(true);
      return;
    }

    setState('EXECUTING');
    try {
      await currentConfig.action();
      
      if (currentConfig.successMessage) {
        setState('SUCCESS');
        if (!currentConfig.keepOpenOnSuccess) {
          setTimeout(() => handleClose(true), 1500);
        }
      } else {
        handleClose(true);
      }
    } catch (e: any) {
      console.error("Action failed", e);
      setErrorMsg(e.message || "An unexpected error occurred during execution.");
      setState('ERROR');
    }
  };

  const handleConfirm = () => {
    if (isAlertMode) {
      handleClose(true);
      return;
    }
    if (config && resolvePromise) {
      executeAction(config, resolvePromise);
    }
  };

  const handleClose = (result: boolean = false) => {
    setIsOpen(false);
    setTimeout(() => {
      setState('CONFIRMING');
      setConfig(null);
      setErrorMsg("");
      setIsAlertMode(false);
    }, 300); // Wait for animation
    if (resolvePromise) {
      resolvePromise(result);
      setResolvePromise(null);
    }
  };

  const getVariantStyles = (variant: ActionVariant = 'default') => {
    switch (variant) {
      case 'danger': return {
        iconBg: 'bg-rose-50 dark:bg-rose-900/20',
        iconColor: 'text-rose-600 dark:text-rose-400',
        buttonBg: 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/20',
        progressColor: 'bg-rose-500'
      };
      case 'warning': return {
        iconBg: 'bg-amber-50 dark:bg-amber-900/20',
        iconColor: 'text-amber-600 dark:text-amber-400',
        buttonBg: 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20 text-white',
        progressColor: 'bg-amber-500'
      };
      default: return {
        iconBg: 'bg-blue-50 dark:bg-blue-900/20',
        iconColor: 'text-blue-600 dark:text-blue-400',
        buttonBg: 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20',
        progressColor: 'bg-blue-500'
      };
    }
  };

  const styles = config ? getVariantStyles(config.variant) : getVariantStyles();

  return (
    <ActionFlowContext.Provider value={{ startAction, openAlert }}>
      {children}
      {isOpen && config && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100 dark:border-slate-800 relative">
            
            {/* Progress Bar for Loading */}
            {state === 'EXECUTING' && (
              <div className="absolute top-0 left-0 w-full h-1 bg-slate-100 dark:bg-slate-800">
                <div className={`h-full ${styles.progressColor} animate-indeterminate`}></div>
              </div>
            )}

            <div className="p-8 space-y-6">
              {/* Header Icon & Title */}
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-colors duration-300 ${
                  state === 'ERROR' ? 'bg-rose-50 text-rose-600' : 
                  state === 'SUCCESS' ? 'bg-emerald-50 text-emerald-600' : 
                  styles.iconBg + ' ' + styles.iconColor
                }`}>
                  {state === 'EXECUTING' ? <Loader2 size={28} className="animate-spin" /> :
                   state === 'SUCCESS' ? <CheckCircle2 size={28} /> :
                   state === 'ERROR' ? <ShieldAlert size={28} /> :
                   config.variant === 'danger' ? <ShieldAlert size={28} /> : 
                   config.variant === 'warning' ? <AlertTriangle size={28} /> : 
                   <Info size={28} />}
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900 dark:text-slate-100 tracking-tight leading-none">
                    {state === 'EXECUTING' ? 'Processing...' :
                     state === 'SUCCESS' ? 'Complete' :
                     state === 'ERROR' ? 'Action Failed' :
                     config.title}
                  </h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                    {state === 'EXECUTING' ? 'Please Wait' : 
                     state === 'SUCCESS' ? 'Confirmed' : 
                     state === 'ERROR' ? 'System Error' : 
                     isAlertMode ? 'Notice' : 'Confirmation Required'}
                  </p>
                </div>
              </div>

              {/* Content Body */}
              <div className="min-h-[3rem]">
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300 leading-relaxed">
                  {state === 'ERROR' ? errorMsg : 
                   state === 'SUCCESS' ? (config.successMessage || "Operation completed successfully.") : 
                   config.message}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                {state === 'CONFIRMING' && (
                  <>
                    {!isAlertMode && (
                      <button 
                        onClick={() => handleClose(false)}
                        className="flex-1 py-3.5 text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors uppercase tracking-wider"
                      >
                        {config.cancelLabel || 'Cancel'}
                      </button>
                    )}
                    <button 
                      onClick={handleConfirm}
                      className={`flex-[1.5] py-3.5 text-white text-xs font-black rounded-xl shadow-lg transition-all uppercase tracking-wider active:scale-95 ${styles.buttonBg}`}
                    >
                      {config.confirmLabel || (isAlertMode ? 'Dismiss' : 'Confirm')}
                    </button>
                  </>
                )}

                {state === 'EXECUTING' && (
                  <button disabled className="w-full py-3.5 bg-slate-100 dark:bg-slate-800 text-slate-400 rounded-xl text-xs font-bold uppercase tracking-wider cursor-wait">
                    Processing...
                  </button>
                )}

                {state === 'SUCCESS' && (
                  <button 
                    onClick={() => handleClose(true)}
                    className="w-full py-3.5 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-lg shadow-emerald-600/20 active:scale-95"
                  >
                    Done
                  </button>
                )}

                {state === 'ERROR' && (
                  <div className="flex gap-3 w-full">
                    <button 
                      onClick={() => handleClose(false)}
                      className="flex-1 py-3.5 text-slate-500 hover:bg-slate-100 rounded-xl text-xs font-bold uppercase tracking-wider"
                    >
                      Close
                    </button>
                    <button 
                      onClick={() => setState('CONFIRMING')}
                      className="flex-1 py-3.5 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-lg active:scale-95"
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </ActionFlowContext.Provider>
  );
};
